import { createContext, useContext, useState, useEffect } from "react";
import api, { saveSession, silentRefresh, lastRefreshWasRejected } from "@/lib/api";
import { configureRevenueCat } from "@/lib/revenuecat";
import { initAppsFlyer } from "@/lib/appsflyer";

const AuthContext = createContext(null);
export { AuthContext };

// Bind the RevenueCat SDK to whichever user we currently know about
// so any purchase (paywall, promo-code sheet, restore) is attributed
// to the RIGHT user. Previously this only ran when NativePaywall
// mounted — but Apple's Offer Code sheet can be triggered from
// Settings too, so we hoist the call to the auth layer. `configure`
// is a no-op on non-iOS (guarded by `isRevenueCatEnabled`).
async function _bindRevenueCatToUser(userId) {
  if (!userId) return;
  try { await configureRevenueCat(userId); }
  catch (e) { console.warn("[auth] configureRevenueCat failed", e); }
  // Boot AppsFlyer at the same time as RC. Native-iOS-only guard is
  // inside — safe to call on web / Android / dev. We do NOT wait for
  // ATT here: the AppsFlyer SDK has `waitForATTUserAuthorization=60`
  // baked in, so its first install payload waits for whichever the
  // user picks at the ATT prompt (fired later from the paywall).
  try { await initAppsFlyer(userId); }
  catch (e) { console.warn("[auth] initAppsFlyer failed", e); }
}

const USER_CACHE = "calofay_user_cache";
function readCachedUser() {
  try { return JSON.parse(localStorage.getItem(USER_CACHE) || "null"); } catch { return null; }
}
// Retry /auth/me with backoff (~30s total) when the server is slow; stops on 401.
async function retryMe(setUser) {
  for (const wait of [2000, 4000, 8000, 16000]) {
    await new Promise((r) => setTimeout(r, wait));
    try { const r = await api.get("/auth/me"); setUser(r.data); return; }
    catch (e) { if (e?.response?.status === 401) return; }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) { try { localStorage.setItem(USER_CACHE, JSON.stringify(user)); } catch (_) {} }
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem("calofay_token");
    if (!token) {
      // No access token (iOS can evict storage): try the device refresh token or the cookie, then remembered creds.
      (async () => {
        const fresh = await silentRefresh();
        if (fresh) {
          try {
            const r = await api.get("/auth/me");
            setUser(r.data); _bindRevenueCatToUser(r.data?.id);
            setLoading(false);
            return;
          } catch (_) { /* fall through to remembered creds */ }
        }
        try {
          const { loadRememberedCreds } = await import("@/lib/remember");
          // After an explicit sign-out, keep the creds for prefill only — no silent sign-in.
          const saved = localStorage.getItem("calofay_signed_out") === "1" ? null : loadRememberedCreds();
          if (saved?.email && saved?.password) {
            const { data } = await api.post("/auth/login", { email: saved.email, password: saved.password });
            saveSession(data); setUser(data.user); _bindRevenueCatToUser(data.user?.id);
          }
        } catch (_) { /* → /login */ }
        setLoading(false);
      })();
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); _bindRevenueCatToUser(r.data?.id); })
      .catch(async (err) => {
        // Slow/overloaded server or no network: NOT a logout. Keep the token,
        // open with the last known profile and retry quietly in the background.
        if (err?.response?.status !== 401 || !lastRefreshWasRejected()) {
          const cached = readCachedUser();
          if (cached) { setUser(cached); retryMe(setUser); }
          else await retryMe(setUser);
          return;
        }
        // Access token dead AND /auth/refresh (cookie) failed. Try the
        // Remember-me credential fallback before giving up — same reason
        // as above (iOS WKWebView drops the refresh cookie on cold start).
        localStorage.removeItem("calofay_token");
        try {
          const { loadRememberedCreds } = await import("@/lib/remember");
          const saved = loadRememberedCreds();
          if (saved?.email && saved?.password) {
            const { data } = await api.post("/auth/login", {
              email: saved.email, password: saved.password,
            });
            saveSession(data);
            setUser(data.user);
            _bindRevenueCatToUser(data.user?.id);
          }
        } catch (_) { /* leave user null → /login */ }
      })
      .finally(() => setLoading(false));
  }, []);

  // NOTE: iOS push registration is intentionally NOT auto-fired here.
  // We used to call `initIOSPush()` right after login → that surfaces
  // Apple's native "Allow Notifications?" dialog at the WORST moment
  // (usually alongside the paywall right after signup), which both
  // hurts opt-in rates and looks pushy. The user-driven flow is:
  //   • Dashboard shows the `PushPromptCard` pre-prompt (Duolingo-style)
  //     ~2 s after landing on Home post-onboarding.
  //   • Or the user opts in via Profile → Notification settings.
  // Both entry points call `initIOSPush()` themselves, which handles
  // permission + APNs register + backend token upsert in one shot.

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveSession(data);
    // Persist credentials for silent iOS cold-start relogin (WKWebView
    // drops the refresh cookie when the OS reaps the process). The Login
    // page still re-saves + honours the Remember-me checkbox for opt-out
    // on shared devices — this only ensures the default path works.
    try {
      const { saveRememberedCreds } = await import("@/lib/remember");
      saveRememberedCreds(email, password);
    } catch (_) { /* non-fatal */ }
    setUser(data.user);
    _bindRevenueCatToUser(data.user?.id);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    saveSession(data);
    localStorage.setItem("calofay_variant", data.user.ab_variant);
    // Persist credentials so an iOS cold-start (which drops the refresh
    // cookie in WKWebView) can silently re-authenticate from localStorage
    // on the next app open. Same behaviour as ticking "Remember me" on the
    // login form — signup users clearly want to stay signed in.
    try {
      if (payload?.email && payload?.password) {
        const { saveRememberedCreds } = await import("@/lib/remember");
        saveRememberedCreds(payload.email, payload.password);
      }
    } catch (_) { /* localStorage full / private mode — non-fatal */ }
    setUser(data.user);
    _bindRevenueCatToUser(data.user?.id);
    return data.user;
  };

  const logout = async () => {
    // Fire-and-forget the server-side revoke so the refresh cookie can't
    // be used again. Local state is cleared unconditionally — even if the
    // network is down, this device is signed out.
    try { await api.post("/auth/logout", { refresh_token: localStorage.getItem("calofay_refresh") }); } catch (_) { /* ignore — device signs out either way */ }
    localStorage.removeItem("calofay_token");
    localStorage.removeItem("calofay_refresh");
    localStorage.removeItem(USER_CACHE);
    // Remember-me creds stay so the login form is prefilled; the flag stops auto sign-in.
    localStorage.setItem("calofay_signed_out", "1");
    setUser(null);
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
